<script setup lang="ts">
import { onMounted, watch, computed } from 'vue';
import { todaycode, transformCodeToDate } from '@/functions/utils';
import { useI18n } from 'vue-i18n'
import AbsorbanceChart from '@/components/AbsorbanceChart.vue'
import CulturesComponent from '@/components/CulturesComponent.vue'
import LiveData from '@/components/LiveData.vue';
import Predictions from '@/components/Predictions.vue';

const { t } = useI18n()

import {
  deactivateCulture,
  reactivateCulture,
  createCulture,
} from '@/functions/functions_cultures';

import { useDataStore } from '@/stores/useDataStore';
const dataStore = useDataStore();

function deactivateCultureHandler() {
  deactivateCulture(t)
}

function reactivateCultureHandler() {
  reactivateCulture(t)
}

function createCultureHandler() {
  createCulture(t)
}

onMounted(async () => {
  dataStore.showCreateCulture = false;
  dataStore.newCulture.startdaycode.startday_code = todaycode;
  dataStore.newCulture.pbr.id = 0
  dataStore.newCulture.specie.id = 0
  dataStore.filtreEspece = null
  dataStore.filtrePBR = null
});

watch(() => dataStore.selectedCulture, (newVal: Culture | null) => {
  if (newVal) {
    const today = new Date();
    const startDateStr = transformCodeToDate(newVal.startdaycode.startday_code); // format YYYY-MM-DD
    const startDate = new Date(startDateStr);
    const age_in_days = Math.floor((today.getTime() - startDate.getTime()) / (1000 * 60 * 60 * 24));

    dataStore.enrichedCulture = {
      ...newVal,
      age_in_days,
      number_of_harvests: newVal.recoltes?.length || 0,
      number_of_samples: newVal.prelevements?.length || 0,
    };
  } else {
    dataStore.enrichedCulture = null;
  }
});

watch(
  () => [dataStore.filtreEspece, dataStore.filtrePBR],
  () => {
    dataStore.selectedCulture = null;
  }
);

const filteredStrains = computed(() => {
  // Trouver l'espèce sélectionnée dans dataStore.species
  const selectedSpecie = dataStore.species.find(
    (specie) => specie.id === dataStore.newCulture.specie.id
  );

  // Si aucune espèce n'est sélectionnée, retourner un tableau vide ou toutes les souches
  const specieCode = selectedSpecie ? selectedSpecie.specie_code : '';

  // Filtrer les souches pour celles commençant par specie_code
  return dataStore.strains.filter((strain) =>
    strain.extended_code.startsWith(specieCode)
  );
});

const recoltes = computed(() =>
  dataStore.selectedCulture?.analyses?.filter(a => a.batch).sort((a, b) => {
    const aDaycodeStr = transformCodeToDate(a.daycode)
    const bDaycodeStr = transformCodeToDate(b.daycode)
    const dateA = new Date(aDaycodeStr);
    const dateB = new Date(bDaycodeStr);
    return dateA.getTime() - dateB.getTime();
  }) || []
);

const prelevements = computed(() =>
  dataStore.selectedCulture?.analyses?.filter(a => a.prelevement).sort((a, b) => {
    const aDaycodeStr = transformCodeToDate(a.daycode)
    const bDaycodeStr = transformCodeToDate(b.daycode)
    const dateA = new Date(aDaycodeStr);
    const dateB = new Date(bDaycodeStr);
    return dateA.getTime() - dateB.getTime();
  }) || []
);

const formatMatiereSeche = (value: number) => value !== null ? value.toFixed(2) + ' %' : '';

import { useAuthStore } from '@/stores/useAuthStore'
import Maintenance from '@/components/Maintenance.vue';
const authStore = useAuthStore();
</script>

<template>
  <Maintenance />
  <div v-if="authStore.isAuthenticated && authStore.scopes.includes('reader')">
    <div>
      <h1>{{ $t('cultureTitle') }}</h1>
    </div>

    <div v-if="dataStore.isDataLoading" class="spinner-container module">
      <div class="spinner"></div>
      <p>{{ $t('loadingData') }}</p>
    </div>

    <div v-else class="module">
      <CulturesComponent />
    </div>

    <div v-if="authStore.scopes.includes('editor') && !dataStore.isDataLoading" class="module">

      <div v-if="!dataStore.showCreateCulture">
        <div class="button-group">
          <button @click="dataStore.showCreateCulture = true">+ {{ $t('newCultureButton') }}</button>
        </div>
      </div>

      <!-- Formulaire de création de culture -->
      <div v-if="dataStore.showCreateCulture" class="form-section" style="padding-top: 25px;">
        <form @submit.prevent="createCultureHandler">
          <select v-model="dataStore.newCulture.pbr.id" class="styled-dropdown">
            <option :value="0" disabled>{{ $t('selectPBR') }}</option>
            <option v-for="p in dataStore.pbrs" :value="p.id" :key="p.id">{{ p.numero_pbr }}</option>
          </select>

          <select v-model="dataStore.newCulture.specie.id" class="styled-dropdown">
            <option :value="0" disabled>{{ $t('selectSpecie') }}</option>
            <option v-for="s in dataStore.species" :value="s.id" :key="s.id">{{ s.specie_code }}</option>
          </select>

          <select v-model="dataStore.newCulture.strain.id" class="styled-dropdown">
            <option :value="0" disabled>{{ $t('selectStrain') }}</option>
            <option v-for="s in filteredStrains" :value="s.id" :key="s.id">{{ s.extended_code }}</option>
          </select>

          <div>
            <label for="startday_code">Startday Code</label>
            <input id="startday_code" v-model="dataStore.newCulture.startdaycode.startday_code" pattern="^\d{2}-\d{3}$"
              :title="$t('startdayCodeInvalid')" class="form-input" :placeholder="todaycode" />
          </div>

          <div>
            <label for="previous_ip_code">{{ $t('previousIpCode') }}</label>
            <input id="previous_ip_code" v-model="dataStore.newCulture.previous_ip_code" class="form-input" />
          </div>

          <div>
            <label for="inoculum_code">{{ $t('inoculumCode') }}</label>
            <input id="inoculum_code" v-model="dataStore.newCulture.inoculum_code" class="form-input" />
          </div>

          <div>
            <label for="inoculum_do">{{ $t('inoculumDo') }}</label>
            <input id="inoculum_do" v-model="dataStore.newCulture.inoculum_do" class="form-input" />
          </div>
          <div class="button-group">
            <button type="submit">{{ $t('create') }}</button>
            <button type="button" @click="dataStore.showCreateCulture = false">{{ $t('cancel') }}</button>
          </div>
        </form>
      </div>
    </div>

    <!-- Informations générales sur la culture -->
    <div v-if="dataStore.selectedCulture" class="module-recoltes">
      <h2>{{ $t('generalInfo') }} ({{ $t('number') }} {{ dataStore.enrichedCulture?.id }})</h2>

      <div style="display: grid; grid-template-columns: repeat(3, 1fr); gap: 1rem;">
        <div>
          <strong>{{ $t('specie') }} :</strong>
          {{ dataStore.enrichedCulture?.specie.specie_code }}
        </div>
        <div><strong>Nom :</strong> {{ dataStore.enrichedCulture?.specie.specie_name }}</div>
        <div><strong>{{ $t('reactor') }} :</strong> {{ dataStore.enrichedCulture?.pbr.numero_pbr }}</div>
        <div><strong>{{ $t('age') }} :</strong> {{ dataStore.enrichedCulture?.age_in_days }} {{ $t('days') }}</div>
        <div><strong>{{ $t('harvestCount') }} :</strong> {{ dataStore.enrichedCulture?.number_of_harvests }}</div>
        <div><strong>{{ $t('sampleCount') }} :</strong> {{ dataStore.enrichedCulture?.number_of_samples }}</div>
      </div>

      <div v-if="authStore.scopes.includes('editor')" class="button-group" style="padding-top: 20px;">
        <button v-if="dataStore.selectedCulture?.active" @click="deactivateCultureHandler">
          {{ $t('deactivate') }}
        </button>
        <button v-else @click="reactivateCultureHandler">
          {{ $t('reactivate') }}
        </button>
      </div>
    </div>

    <!-- Informations sur la souche -->
    <div v-if="dataStore.selectedCulture" class="module-recoltes">
      <h2>Souche: {{ dataStore.enrichedCulture?.strain.extended_code }}
        ({{ dataStore.enrichedCulture?.strain.origin }})
      </h2>

      <div style="display: grid; grid-template-columns: repeat(3, 1fr); gap: 1rem;">
        <div><strong>{{ $t('domain') }} :</strong> {{ dataStore.enrichedCulture?.strain.domain }}</div>
        <div><strong>{{ $t('kingdom') }} :</strong> {{ dataStore.enrichedCulture?.strain.kingdom }}</div>
        <div><strong>{{ $t('phylum') }} :</strong> {{ dataStore.enrichedCulture?.strain.phylum }}</div>
        <div><strong>{{ $t('specie_class') }} :</strong> {{ dataStore.enrichedCulture?.strain.specie_class }}</div>
        <div><strong>{{ $t('superorder') }} :</strong> {{ dataStore.enrichedCulture?.strain.superorder }}</div>
        <div><strong>{{ $t('specie_order') }} :</strong> {{ dataStore.enrichedCulture?.strain.specie_order }}</div>
        <div><strong>{{ $t('family') }} :</strong> {{ dataStore.enrichedCulture?.strain.family }}</div>
        <div><strong>{{ $t('genus') }} :</strong> {{ dataStore.enrichedCulture?.strain.genus }}</div>
        <div><strong>{{ $t('specie') }} :</strong> {{ dataStore.enrichedCulture?.strain.species }}</div>
      </div>
    </div>

    <LiveData v-if="dataStore.selectedCulture" />

    <!-- Affichage des analyses pour la culture sélectionnée -->
    <div v-if="dataStore.selectedCulture" class="module-recoltes">
      <h2>{{ $t('analysisBisTitle') }}</h2>

      <!-- Tableau des Récoltes -->
      <h3>{{ $t('harvestBisTitle') }}</h3>
      <table v-if="recoltes.length" class="table-recoltes">
        <thead>
          <tr>
            <th class="recoltes small">Date</th>
            <th class="recoltes small">Référence</th>
            <th class="recoltes small">Provenance</th>
            <th class="recoltes small">Concentration</th>
            <th class="recoltes small">Matière Sèche</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="analyse in recoltes" :key="analyse.id">
            <td>{{ transformCodeToDate(analyse.daycode) }}</td>
            <td>{{ analyse.ref }}</td>
            <td>Batch {{ analyse.batch.batch_code }}</td>
            <td>{{ dataStore.getConcentration(analyse) !== '' ? dataStore.getConcentration(analyse) + ' %' : '' }}</td>
            <td>{{ analyse.matiere_seche !== null ? formatMatiereSeche(analyse.matiere_seche) : '' }}</td>
          </tr>
        </tbody>
      </table>
      <p v-else>{{ $t('noAnalyzedHarvest') }}</p>

      <!-- Tableau des Prélèvements -->
      <h3>{{ $t('sampleBisTitle') }}</h3>
      <table v-if="prelevements.length" class="table-recoltes">
        <thead>
          <tr>
            <th class="recoltes small">{{ $t('date') }}</th>
            <th class="recoltes small">{{ $t('reference') }}</th>
            <th class="recoltes small">{{ $t('origin') }}</th>
            <th class="recoltes small">{{ $t('concentration') }}</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="analyse in prelevements" :key="analyse.id">
            <td>{{ transformCodeToDate(analyse.daycode) }}</td>
            <td>{{ analyse.ref }}</td>
            <td>{{ $t('sample') }} {{ analyse.prelevement.daycode }}</td>
            <td>{{ dataStore.getConcentration(analyse) !== '' ? dataStore.getConcentration(analyse) + ' %' : '' }}</td>
          </tr>
        </tbody>
      </table>
      <p v-else>{{ $t('noAnalyzedSample') }}</p>
    </div>

    <!-- Section pour l'évolution de l'absorbance -->
    <div v-if="dataStore.selectedCulture" class="chart">
      <h2>{{ $t('chartTitle') }}</h2>
      <div v-if="prelevements.length || recoltes.length">
        <AbsorbanceChart :prelevements="dataStore.selectedCulture?.prelevements || []"
          :analyses="dataStore.selectedCulture?.analyses || []" />
      </div>
      <div v-else>
        <p>{{ $t('noData') }}</p>
      </div>
    </div>

    <!-- Bloc module dédié aux prédictions -->
    <Predictions v-if="dataStore.selectedCulture" />

  </div>

  <div v-else class="centered-block">
    <h2>{{ $t('loginRequiredTitle') }}</h2>
    <p>{{ $t('loginRequiredMessage') }}</p>
  </div>
</template>